// ============================================================================
// apps/web/src/components/ui/password-input.tsx
// ----------------------------------------------------------------------------
// Input password avec bouton œil pour voir/masquer le contenu. Wrapper autour
// du composant Input — accepte toutes les mêmes props sauf `type` (forcé).
//
// Usage :
//   <PasswordInput value={pw} onChange={(e) => setPw(e.target.value)} required />
// ============================================================================

import { forwardRef, useState, type ComponentPropsWithoutRef } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Input } from './input';
import { cn } from '@/lib/utils';

type Props = Omit<ComponentPropsWithoutRef<typeof Input>, 'type'>;

export const PasswordInput = forwardRef<HTMLInputElement, Props>(
  ({ className, ...props }, ref) => {
    const [visible, setVisible] = useState(false);
    return (
      <div className="relative">
        <Input
          ref={ref}
          type={visible ? 'text' : 'password'}
          className={cn('pr-10', className)}
          {...props}
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? 'Masquer le mot de passe' : 'Voir le mot de passe'}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
        >
          {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    );
  },
);
PasswordInput.displayName = 'PasswordInput';
